/**
 * Psikometrik profil düzenleyici — personanın mizacı.
 *
 * Eksen kataloğu SUNUCUDAN gelir (`GET /persona-library/axes`), burada
 * hardcode edilmez: backend'e yeni bir eksen eklemek bu ekranda bir kod
 * değişikliği olmadan görünür olsun diye. Katalog `psychometrics.py`'deki
 * `describe_axes()` çıktısıdır.
 *
 * Etkileşim tasarımı iki gözlemden doğdu:
 *   • 50 nötrdür ve prompt'a HİÇBİR ŞEY eklemez. Kullanıcının hangi
 *     ekseni gerçekten "açtığını" görmesi gerekir — bu yüzden aktif
 *     eksenler renklenir ve başlıkta sayılır.
 *   • Backend 12'lik bir ölü bant uygular (50±12 arası etkisizdir). Slider
 *     ölü bandın içindeyken kaydırıcı gri kalır, dışına çıkınca renklenir;
 *     böylece "kaydırdım ama bir şey değişmedi" şaşkınlığı ortadan kalkar.
 */
import { useMemo } from "react";
import {
  AXIS_GROUP_LABELS,
  AXIS_GROUP_ORDER,
  isAxisActive,
  type PsychometricAxis,
  type PsychometricProfile,
} from "../../domain/personaLibrary";
import { Btn, Chip, SectionTitle } from "./ui";

/** Backend `_BAND_QUALIFIER` ile aynı eşikler — etiket tutarlılığı için. */
function intensityLabel(value: number): string {
  const d = Math.abs(value - 50);
  if (d <= 12) return "nötr";
  if (d < 20) return "hafif";
  if (d < 30) return "ölçülü";
  if (d < 42) return "belirgin";
  return "uç";
}

function AxisRow({
  axis,
  value,
  onChange,
  disabled,
}: {
  axis: PsychometricAxis;
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const active = isAxisActive(value);
  const leaning = value > 50 ? axis.highLabel : axis.lowLabel;

  return (
    <div className="space-y-1.5 py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12.5px] font-medium text-slate-200" title={axis.description}>
          {axis.label}
        </span>
        {active ? (
          <Chip tone="violet" title={axis.description}>
            {intensityLabel(value)} {leaning.toLocaleLowerCase("tr-TR")}
          </Chip>
        ) : (
          <span className="text-[10.5px] text-slate-600">etkisiz</span>
        )}
      </div>

      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={axis.label}
        className={[
          "w-full cursor-pointer appearance-none rounded-full",
          "h-1.5 bg-white/10 disabled:cursor-not-allowed disabled:opacity-50",
          "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3.5",
          "[&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:rounded-full",
          "[&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-white/30",
          "[&::-webkit-slider-thumb]:transition-colors",
          active
            ? "[&::-webkit-slider-thumb]:bg-violet-400"
            : "[&::-webkit-slider-thumb]:bg-slate-500",
          "[&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5",
          "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0",
          active ? "[&::-moz-range-thumb]:bg-violet-400" : "[&::-moz-range-thumb]:bg-slate-500",
        ].join(" ")}
      />

      <div className="flex items-center justify-between text-[10.5px] text-slate-500">
        <span>{axis.lowLabel}</span>
        <span className="tabular-nums text-slate-600">{value}</span>
        <span>{axis.highLabel}</span>
      </div>
    </div>
  );
}

export function PsychometricEditor({
  axes,
  profile,
  onChange,
  disabled,
}: {
  axes: PsychometricAxis[];
  profile: PsychometricProfile;
  onChange: (next: PsychometricProfile) => void;
  disabled?: boolean;
}) {
  const grouped = useMemo(() => {
    const map = new Map<PsychometricAxis["group"], PsychometricAxis[]>();
    for (const axis of axes) {
      const bucket = map.get(axis.group) ?? [];
      bucket.push(axis);
      map.set(axis.group, bucket);
    }
    return map;
  }, [axes]);

  const activeCount = axes.filter((a) => isAxisActive(profile[a.key] ?? 50)).length;

  function setAxis(key: string, value: number) {
    onChange({ ...profile, [key]: value });
  }

  function resetAll() {
    const next: PsychometricProfile = {};
    for (const a of axes) next[a.key] = 50;
    onChange(next);
  }

  return (
    <div className="space-y-4">
      <SectionTitle
        title="Mizaç Profili"
        hint={
          activeCount === 0
            ? "Tüm eksenler nötr — persona yalnızca talimatıyla davranır. Bir ekseni ortadan uzaklaştırdıkça karakteri belirginleşir."
            : `${activeCount} eksen aktif. Yalnız aktif eksenler system prompt'a davranış kuralı olarak eklenir; en güçlü 7 tanesi kullanılır.`
        }
        action={
          <Btn size="sm" tone="ghost" onClick={resetAll} disabled={disabled || activeCount === 0}>
            Sıfırla
          </Btn>
        }
      />

      {activeCount > 7 && (
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2 text-[11.5px] leading-relaxed text-amber-200">
          {activeCount} eksen aktif; prompt'a yalnızca <strong>nötrden en uzak 7 tanesi</strong>{" "}
          girer. Bu sınır bilinçlidir — aynı anda çok sayıda davranış kuralı verildiğinde
          modeller bir kısmını sessizce yok sayar, ki bu hiç kural vermemekten kötüdür.
        </div>
      )}

      <div className="space-y-5">
        {AXIS_GROUP_ORDER.map((group) => {
          const items = grouped.get(group);
          if (!items || items.length === 0) return null;
          return (
            <section key={group}>
              <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {AXIS_GROUP_LABELS[group]}
              </h4>
              <div className="divide-y divide-white/[0.05] rounded-xl border border-white/[0.07] bg-white/[0.015] px-3">
                {items.map((axis) => (
                  <AxisRow
                    key={axis.key}
                    axis={axis}
                    value={profile[axis.key] ?? 50}
                    onChange={(v) => setAxis(axis.key, v)}
                    {...(disabled ? { disabled: true } : {})}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
